import type { JSX, ReactNode } from "react";

/**
 * Stroke-icon set for every piece of UI chrome that used to ship an
 * emoji inside a translated string. Emoji render differently on every
 * platform, ignore `color` and `font-weight`, and survive a re-skin
 * unchanged — so they cannot belong to a themeable design system.
 * These draw on a 24-unit grid in `currentColor`, which means an icon
 * inherits the colour of whatever chrome hosts it.
 *
 * User-authored content (the chat reaction presets) keeps its emoji:
 * that is a message, not chrome.
 */
export type IconName =
  | "alert-circle"
  | "alert-triangle"
  | "arrow-left"
  | "arrow-right"
  | "arrow-up"
  | "bell"
  | "book"
  | "cards"
  | "chart"
  | "check"
  | "chevron-down"
  | "chevron-right"
  | "chevron-up"
  | "chip"
  | "clipboard"
  | "clock"
  | "close"
  | "dice"
  | "door"
  | "eye"
  | "flag"
  | "flame"
  | "globe"
  | "help"
  | "info"
  | "keyboard"
  | "life-buoy"
  | "link"
  | "lock"
  | "menu"
  | "message"
  | "moon"
  | "palette"
  | "pause"
  | "play"
  | "refresh"
  | "ruler"
  | "scroll"
  | "search"
  | "seat"
  | "settings"
  | "shield"
  | "smartphone"
  | "spade"
  | "target"
  | "trophy"
  | "user"
  | "users"
  | "volume-off"
  | "volume-on"
  | "wallet"
  | "wallet-empty";

const PATHS: Readonly<Record<IconName, ReactNode>> = {
  "alert-circle": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16.5v.01" />
    </>
  ),
  "alert-triangle": (
    <>
      <path d="M10.3 4.3 2.8 17.2A2 2 0 0 0 4.5 20h15a2 2 0 0 0 1.7-2.8L13.7 4.3a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17v.01" />
    </>
  ),
  "arrow-left": <path d="M19 12H5M11 18l-6-6 6-6" />,
  "arrow-right": <path d="M5 12h14M13 6l6 6-6 6" />,
  "arrow-up": <path d="M12 19V5M6 11l6-6 6 6" />,
  bell: (
    <>
      <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6Z" />
      <path d="M10.3 19a2 2 0 0 0 3.4 0" />
    </>
  ),
  book: (
    <>
      <path d="M3 5.5A2.5 2.5 0 0 1 5.5 3H12v16H5.5A2.5 2.5 0 0 0 3 21.5Z" />
      <path d="M21 5.5A2.5 2.5 0 0 0 18.5 3H12v16h6.5a2.5 2.5 0 0 1 2.5 2.5Z" />
    </>
  ),
  cards: (
    <>
      <rect
        x="3"
        y="6"
        width="11"
        height="15"
        rx="2"
        transform="rotate(-8 8.5 13.5)"
      />
      <rect
        x="10"
        y="4"
        width="11"
        height="15"
        rx="2"
        transform="rotate(8 15.5 11.5)"
      />
    </>
  ),
  chart: (
    <>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </>
  ),
  check: <path d="m5 12.5 5 5L19 6.5" />,
  "chevron-down": <path d="m6 9 6 6 6-6" />,
  "chevron-right": <path d="m9 6 6 6-6 6" />,
  "chevron-up": <path d="m6 15 6-6 6 6" />,
  chip: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
    </>
  ),
  clipboard: (
    <>
      <rect x="8" y="3" width="8" height="4" rx="1" />
      <path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.2l3.2 2" />
    </>
  ),
  close: <path d="M18 6 6 18M6 6l12 12" />,
  dice: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M8.5 8.5v.01M15.5 8.5v.01M12 12v.01M8.5 15.5v.01M15.5 15.5v.01" />
    </>
  ),
  door: (
    <>
      <path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h8" />
      <path d="M11 12h10M18 8.5l3.5 3.5-3.5 3.5" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  flag: (
    <>
      <path d="M5 21V4M5 5h11l-2 3.5L16 12H5" />
    </>
  ),
  flame: (
    <path d="M12 22c3.6 0 6-2.4 6-5.8 0-3.6-3-5.6-3.6-9.2-1.8 1-2.6 2.6-2.6 4.4 0 1-.7 1.8-1.5 1.2C9.6 11.8 9 10.4 9 9c-1.8 1.6-3 3.8-3 6.4C6 19.4 8.4 22 12 22Z" />
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.4 2.6 3.6 5.6 3.6 9S14.4 18.4 12 21c-2.4-2.6-3.6-5.6-3.6-9S9.6 5.6 12 3Z" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.4 2.3c-.6.3-.9.8-.9 1.4v.4M12 16.5v.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 7.5v.01" />
    </>
  ),
  keyboard: (
    <>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <path d="M6 10v.01M10 10v.01M14 10v.01M18 10v.01M8 14h8" />
    </>
  ),
  "life-buoy": (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4" />
      <path d="m5.6 5.6 3.6 3.6M14.8 14.8l3.6 3.6M18.4 5.6l-3.6 3.6M9.2 14.8l-3.6 3.6" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a4 4 0 0 0 5.7.3l3-3A4 4 0 0 0 13 4.7l-1.7 1.7" />
      <path d="M14 11a4 4 0 0 0-5.7-.3l-3 3A4 4 0 0 0 11 19.3l1.7-1.7" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  menu: <path d="M3 6h18M3 12h18M3 18h18" />,
  message: (
    <path d="M21 11.5a7.5 7.5 0 0 1-7.5 7.5H8l-4 3v-3.9A7.5 7.5 0 0 1 8 4.5h5.5A7.5 7.5 0 0 1 21 11.5Z" />
  ),
  moon: <path d="M20 14.5A8.2 8.2 0 0 1 9.5 4 8.5 8.5 0 1 0 20 14.5Z" />,
  palette: (
    <>
      <path d="M12 21a9 9 0 1 1 9-9c0 2-1.7 3-3.3 3H16a2 2 0 0 0-1.4 3.4c.4.4.4 1.1 0 1.5-.7.7-1.7 1.1-2.6 1.1Z" />
      <path d="M7.5 12v.01M9.5 8v.01M14 7v.01M17 10v.01" />
    </>
  ),
  pause: <path d="M9 5v14M15 5v14" />,
  play: <path d="M7 4.5v15l13-7.5Z" />,
  refresh: (
    <>
      <path d="M20 12a8 8 0 1 1-2.6-5.9" />
      <path d="M20 4v5h-5" />
    </>
  ),
  ruler: (
    <>
      <rect
        x="2"
        y="8"
        width="20"
        height="8"
        rx="1.5"
        transform="rotate(-45 12 12)"
      />
      <path d="M9 8.5 10.5 10M12 5.5 13.5 7M15 2.5 16.5 4" />
    </>
  ),
  scroll: (
    <>
      <path d="M5 4h11a2 2 0 0 1 2 2v12a2 2 0 0 0 2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 0-2-2Z" />
      <path d="M9 8h6M9 12h6" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-4-4" />
    </>
  ),
  seat: (
    <>
      <path d="M6 4v7a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4" />
      <path d="M4 13h16M7 13v7M17 13v7" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </>
  ),
  shield: <path d="M12 3 5 6v5.5c0 4.3 2.9 7.6 7 9.5 4.1-1.9 7-5.2 7-9.5V6Z" />,
  smartphone: (
    <>
      <rect x="6" y="2" width="12" height="20" rx="2.5" />
      <path d="M11 18.5h2" />
    </>
  ),
  spade: (
    <path d="M12 3c-2 3-7 5.6-7 9.3A3.7 3.7 0 0 0 11 15c0 2-.7 3.4-2 4.5h6c-1.3-1.1-2-2.5-2-4.5a3.7 3.7 0 0 0 6-2.7C19 8.6 14 6 12 3Z" />
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="0.75" />
    </>
  ),
  trophy: (
    <>
      <path d="M7 4h10v5a5 5 0 0 1-10 0Z" />
      <path d="M7 6H4.5a2.5 2.5 0 0 0 2.5 4M17 6h2.5a2.5 2.5 0 0 1-2.5 4" />
      <path d="M10 14v3h4v-3M8 20h8" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M5 20.5a7 7 0 0 1 14 0" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16.5 5.2a3.5 3.5 0 0 1 0 5.6M18 14.6a6 6 0 0 1 3 5.4" />
    </>
  ),
  "volume-off": (
    <>
      <path d="M11 5 6.5 9H3v6h3.5L11 19Z" />
      <path d="m16 10 5 4M21 10l-5 4" />
    </>
  ),
  "volume-on": (
    <>
      <path d="M11 5 6.5 9H3v6h3.5L11 19Z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
    </>
  ),
  wallet: (
    <>
      <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H18v3" />
      <rect x="3" y="8" width="18" height="12" rx="2.5" />
      <path d="M16.5 14v.01" />
    </>
  ),
  "wallet-empty": (
    <>
      <rect x="3" y="8" width="18" height="12" rx="2.5" />
      <path d="m8 12 8 4M16 12l-8 4" />
    </>
  ),
};

export interface IconProps {
  readonly name: IconName;
  /** Rendered box in px. Defaults to 20 — the inline-with-text size. */
  readonly size?: number;
  readonly className?: string;
}

export function Icon({ name, size = 20, className }: IconProps): JSX.Element {
  return (
    <svg
      className={className === undefined ? "icon" : `icon ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}

export interface IconLabelProps {
  readonly name: IconName;
  readonly size?: number;
  readonly children: ReactNode;
}

/**
 * An icon paired with its label. The wrapper owns the gap so the pair
 * lines up the same way inside a flex row, a button, or running text.
 */
export function IconLabel({
  name,
  size = 18,
  children,
}: IconLabelProps): JSX.Element {
  return (
    <span className="icon-label">
      <Icon name={name} size={size} />
      {children}
    </span>
  );
}
